import { useEffect, useMemo, useState } from 'react';

export type PaginationManager<T> = {
  pages: T[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
};

export const usePaginationManager = <T>({
  fetchNextPage,
  hasNextPage,
  pages,
  isFetchingNextPage,
}: PaginationManager<T>) => {
  const [pageIndex, setIndexPage] = useState(0);

  const page = useMemo(() => {
    return pages[pageIndex] || undefined;
  }, [pageIndex, pages]);

  const goToNextPage = () => {
    if (hasNextPage && pageIndex === pages.length - 1 && !isFetchingNextPage) {
      fetchNextPage();
    }

    setIndexPage((prev) => prev + 1);
  };

  const goToPreviousPage = () => {
    if (pageIndex) {
      setIndexPage((prev) => prev - 1);
    }
  };

  const reset = () => {
    setIndexPage(0);
  };

  useEffect(() => {
    if (pageIndex === pages.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [pages, pageIndex, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    nextPageEnabled: hasNextPage || pageIndex < pages.length - 1,
    previousPageEnabled: pageIndex > 0,
    page,
    goToNextPage,
    goToPreviousPage,
    reset,
  };
};
